import { useCallback, useRef, useState } from 'react';
import { hasMediaDevices, insecureMediaMessage, screenShareUnsupportedMessage } from '../../../utils/mediaSupport';

/**
 * Screen sharing for proctoring. The candidate MUST share their ENTIRE screen (not a single
 * window or browser tab) — we validate the captured track's `displaySurface` and reject anything
 * that isn't a full monitor, telling the caller to ask again.
 *
 * Because the browser can't force the picker to monitor-only, we request with a `monitor` hint and
 * then verify after the fact. If the candidate stops the share from the browser's own UI, `onEnded`
 * fires so the room can require them to re-share before continuing.
 *
 *   const screen = useScreenShare();
 *   const r = await screen.start();
 *   if (!r.ok) showWarning(r.reason);   // 'not_entire_screen' | 'denied' | 'error'
 */
export default function useScreenShare({ onEnded } = {}) {
  const streamRef = useRef(null);
  const generationRef = useRef(0);
  const pendingStartRef = useRef(null);
  const onEndedRef = useRef(onEnded);
  onEndedRef.current = onEnded;
  const [stream, setStream] = useState(null);
  const [sharing, setSharing] = useState(false);

  const stop = useCallback(() => {
    generationRef.current += 1;
    pendingStartRef.current = null;
    try { streamRef.current && streamRef.current.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    streamRef.current = null;
    setStream(null);
    setSharing(false);
  }, []);

  const start = useCallback(async () => {
    if (pendingStartRef.current) return pendingStartRef.current;
    if (!hasMediaDevices()) {
      // No mediaDevices at all → genuinely insecure context (plain http on a LAN IP, etc.).
      return { ok: false, reason: 'insecure', error: insecureMediaMessage() };
    }
    if (typeof navigator.mediaDevices.getDisplayMedia !== 'function') {
      // mediaDevices EXISTS (camera/mic work → context IS secure), but the browser doesn't expose
      // screen capture. This is the mobile case — do NOT blame the connection; tell them to use a laptop.
      return { ok: false, reason: 'unsupported', error: screenShareUnsupportedMessage() };
    }
    const generation = ++generationRef.current;
    const pending = (async () => {
      try {
        const current = streamRef.current;
        if (current?.getVideoTracks().some((track) => track.readyState === 'live')) {
          setStream(current);
          setSharing(true);
          return { ok: true, surface: current.getVideoTracks()[0]?.getSettings?.().displaySurface || '' };
        }
        try { current?.getTracks().forEach((track) => track.stop()); } catch { /* ignore */ }
        streamRef.current = null;
        setStream(null);
        setSharing(false);

        const s = await navigator.mediaDevices.getDisplayMedia({
          video: { displaySurface: 'monitor', frameRate: { ideal: 8, max: 15 } },
          // Request SYSTEM audio so it can be muxed into the screen recording (the candidate must tick
          // "Share system audio" in the picker; not offered for full-screen on every OS — mic still mixes in).
          audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
          // Chrome hint: surface entire-screen choices and avoid defaulting to the current tab.
          monitorTypeSurfaces: 'include',
          surfaceSwitching: 'exclude',
          selfBrowserSurface: 'exclude',
        });
        if (generation !== generationRef.current) {
          s.getTracks().forEach((track) => track.stop());
          return { ok: false, reason: 'cancelled' };
        }

        const track = s.getVideoTracks()[0];
        const surface = (track.getSettings && track.getSettings().displaySurface) || '';

        // Must be the whole monitor. When the browser reports a surface, require 'monitor' (reject
        // window / browser tab / application). Only when it omits displaySurface entirely do we accept
        // (browser limitation) — Chromium reports it, so this enforces entire-screen there.
        if (surface && surface !== 'monitor') {
          s.getTracks().forEach((t) => t.stop());
          return { ok: false, reason: 'not_entire_screen', surface };
        }

        streamRef.current = s;
        setStream(s);
        setSharing(true);

        // The candidate clicked the browser's "Stop sharing" — require a re-share.
        track.addEventListener('ended', () => {
          if (streamRef.current !== s) return;
          streamRef.current = null;
          setStream(null);
          setSharing(false);
          if (onEndedRef.current) onEndedRef.current();
        }, { once: true });

        return { ok: true, surface };
      } catch (e) {
        if (generation !== generationRef.current) return { ok: false, reason: 'cancelled' };
        if (e && e.name === 'NotAllowedError') return { ok: false, reason: 'denied' };
        return { ok: false, reason: 'error', error: (e && e.message) || String(e) };
      }
    })();
    pendingStartRef.current = pending;
    try { return await pending; }
    finally { if (pendingStartRef.current === pending) pendingStartRef.current = null; }
  }, []);

  return { stream, sharing, start, stop };
}
